import axios from 'axios'
import { injectable } from 'inversify'
import type { ExtractionResultDto } from '@domain/job/JobDto'
import { API_URL, DEFAULT_REQUEST_TIMEOUT, STORAGE_KEYS } from '../AppConstants'

/**
 * Extração para o playground.
 *
 * Bate no `POST /v1/extract` **de verdade** — o mesmo caminho que os produtos usam — e não num
 * endpoint especial de admin. É o que dá valor ao playground: o que se vê aqui é exatamente o
 * que o consumidor veria, incluindo cobrança de UPs e cota.
 *
 * Por isso a credencial é uma **API key de tenant**, não a chave de admin: `/v1/extract` exige
 * `X-API-Key`. Usa axios direto para não passar pelo interceptor que injeta `X-Admin-Key`.
 */
@injectable()
export class DoclenzExtractService {
  /** A chave do playground guardada no navegador, se já houver uma. */
  static playgroundKey(): string | null {
    return localStorage.getItem(STORAGE_KEYS.PLAYGROUND_KEY)
  }

  static setPlaygroundKey(apiKey: string): void {
    localStorage.setItem(STORAGE_KEYS.PLAYGROUND_KEY, apiKey)
  }

  static clearPlaygroundKey(): void {
    localStorage.removeItem(STORAGE_KEYS.PLAYGROUND_KEY)
  }

  /**
   * @param schema JSON Schema opcional. Quando presente, o backend faz extração estruturada e
   *     devolve `structured` — é o contrato que o ADR-027 do VendaX consome.
   */
  async extract(
    file: File,
    options: { schema?: string; pipeline?: string; extractor?: string } = {},
  ): Promise<ExtractionResultDto> {
    const apiKey = DoclenzExtractService.playgroundKey()
    if (!apiKey) {
      throw new Error('Nenhuma chave de playground configurada.')
    }

    const form = new FormData()
    form.append('file', file)
    if (options.schema) form.append('schema', options.schema)
    if (options.pipeline) form.append('pipeline', options.pipeline)
    // Motor por requisição (ADR-007 D-8). Vai como query param porque é assim que a API o lê;
    // exige a feature extractor_choice na chave, senão a resposta é DL-011.
    const query = options.extractor ? `?extractor=${encodeURIComponent(options.extractor)}` : ''

    const response = await axios.post<ExtractionResultDto>(`${API_URL}/v1/extract${query}`, form, {
      headers: { 'X-API-Key': apiKey },
      timeout: DEFAULT_REQUEST_TIMEOUT,
    })
    return response.data
  }
}
